import DeleteIcon from '@mui/icons-material/Delete'
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined'
import PreviewOutlinedIcon from '@mui/icons-material/PreviewOutlined'
import {
    Alert,
    AlertTitle,
    Button,
    CircularProgress,
    Collapse,
    Dialog,
    IconButton,
    TextField,
    Tooltip,
} from '@mui/material'
import cn from 'classnames'
import { useEffect, useState } from 'react'
import config from '../../../config.json'
import Link from '../../common/util/input/Link'
import { ImageUploadButton } from '../../imageupload/ImageUploadButton'
import { DropAlert } from '../DropAlert'
import { DropLink } from '../DropLink'
import { DropPaper } from '../DropPaper'
import MainButton from '../../common/util/input/MainButton'
import InputField from '../../common/util/InputField'
import { checkCID, ipfsToCID } from '../../helpers/Validators'

const PreviewImage = ({
    attributes,
    ipfs,
    selectedAddValueAttribute,
    additionalLayers,
    layersLoaded,
    setLayersLoaded,
}) => {
    if (!selectedAddValueAttribute) {
        return null
    }

    const layers = []
    let curAttribute = attributes.find(
        (element) => element.attribute_name === selectedAddValueAttribute,
    )

    layers.push({
        ipfs,
        layer: curAttribute.possible_values[0].layers[0].layer,
    })
    for (const additionalLayer of additionalLayers) {
        layers.push({
            ipfs: additionalLayer.ipfs,
            layer: additionalLayer.layer,
        })
    }
    layers.sort((a, b) => {
        return a.layer - b.layer
    })

    return (
        <div
            className={cn(
                'w-24 h-24 bg-gradient-to-br rounded-md relative',
                'from-slate-200 to-slate-400',
                'hover:from-slate-100 hover:to-slate-300',
            )}
        >
            {layers.map((layer, index) => (
                <img
                    src={config.ipfs + layer.ipfs}
                    id={'img_layer_' + index}
                    className="object-contain h-full w-full absolute"
                    onLoad={() => {
                        const temp = [...layersLoaded]
                        temp.push(layer.layer)
                        setLayersLoaded(temp)
                    }}
                />
            ))}
        </div>
    )
}

export const AddAttributeValueDialog = ({
    attributes,
    setAttributes,
    blockRules,
    setBlockRules,
    selectedAddValueAttribute,
    setSelectedAddValueAttribute,
    selectedEditValue,
    setSelectedEditValue,
}) => {
    const MAX_IMAGE_WIDTH_HEIGHT = 2000

    const [baseLayer, setBaseLayer] = useState(null)
    const [value, setValue] = useState(null)
    const [ipfs, setIpfs] = useState(null)
    const [layersLoaded, setLayersLoaded] = useState([])
    const [mainImageDims, setMainImageDims] = useState(null)
    const [error, setError] = useState(null)
    const [progressPercentage, setProgressPercentage] = useState(null)
    const [isUploading, setIsUploading] = useState(false)

    const [imageThatIsTooBigExists, setImageThatIsTooBigExists] =
        useState(false)

    const [additionalLayers, setAdditionalLayers] = useState([])

    const addValue = (value, ipfs) => {
        const temp = [...attributes]
        let curAttribute = temp.find(
            (element) => element.attribute_name === selectedAddValueAttribute,
        )

        curAttribute.possible_values.push({
            value,
            chance: 1,
            guaranteed: false,
            layers: [
                {
                    ipfs,
                    layer: curAttribute.possible_values[0].layers[0].layer,
                    width: mainImageDims?.width,
                    height: mainImageDims?.height,
                },
                ...additionalLayers,
            ],
        })

        // If the user adds a new value without having modified the 'none' value, we delete the 'none' value
        if (
            curAttribute.possible_values.length === 2 &&
            curAttribute.possible_values[0].value === 'none' &&
            curAttribute.possible_values[0].chance === 0
        ) {
            curAttribute.possible_values.splice(0, 1)
        }
        setAttributes(temp)
    }

    const updateValue = () => {
        const tempAttributes = [...attributes]
        const tempBlockRules = [...blockRules]

        const curAttribute = tempAttributes.find(
            (element) => element.attribute_name === selectedAddValueAttribute,
        )
        const curValue = curAttribute.possible_values.find(
            (element) => element.value === selectedEditValue,
        )
        curValue.value = value

        curValue.layers = [
            {
                ipfs,
                layer: baseLayer,
                width: mainImageDims?.width,
                height: mainImageDims?.height,
            },
            ...additionalLayers,
        ]

        // Max 1 Base Attribute in the block list, e.g. this value is incompatible with x
        const baseBlockRule = tempBlockRules.find((element) => {
            return (
                element.base_attribute.attribute_name ===
                    selectedAddValueAttribute &&
                element.base_attribute.value === selectedEditValue
            )
        })
        if (baseBlockRule) {
            baseBlockRule.base_attribute.value = value
        }

        // Any other attribute can have this value as a blacklisted attribute
        for (const rule of tempBlockRules) {
            const blacklistedAttribute = rule.attribute_blacklist.find(
                (element) => {
                    return (
                        element.attribute_name === selectedAddValueAttribute &&
                        element.value === selectedEditValue
                    )
                },
            )
            if (blacklistedAttribute) {
                blacklistedAttribute.value = value
            }
        }

        setAttributes(tempAttributes)
        setBlockRules(tempBlockRules)
    }

    const addAdditionalLayer = () => {
        const temp = [...additionalLayers]
        temp.push({ ipfs: '', layer: 0, width: null, height: null })
        setAdditionalLayers(temp)
    }

    const removeAdditionalLayer = (index) => {
        const temp = [...additionalLayers]
        temp.splice(index, 1)
        setAdditionalLayers(temp)
    }

    const updateAdditionalLayerIpfs = (index, ipfs) => {
        const temp = [...additionalLayers]
        const targetLayer = temp[index]
        targetLayer.ipfs = ipfs
        setAdditionalLayers(temp)
    }

    const updateAdditionalLayerLayer = (index, layer) => {
        const temp = [...additionalLayers]
        const targetLayer = temp[index]
        targetLayer.layer = layer
        setAdditionalLayers(temp)
    }

    const deleteValue = () => {
        const tempAttributes = [...attributes]
        const tempBlockRules = [...blockRules]

        const curAttribute = tempAttributes.find(
            (element) => element.attribute_name === selectedAddValueAttribute,
        )
        const valueIndex = curAttribute.possible_values.findIndex(
            (element) => element.value === selectedEditValue,
        )
        curAttribute.possible_values.splice(valueIndex, 1)

        // Max 1 Base Attribute in the block list, e.g. this value is incompatible with x
        const baseBlockRuleIndex = tempBlockRules.findIndex((element) => {
            return (
                element.base_attribute.attribute_name ===
                    selectedAddValueAttribute &&
                element.base_attribute.value === selectedEditValue
            )
        })
        if (baseBlockRuleIndex !== -1) {
            tempBlockRules.splice(baseBlockRuleIndex, 1)
        }

        // Any other attribute can have this value as a blacklisted attribute
        for (const rule of tempBlockRules) {
            const blacklistedAttributeIndex =
                rule.attribute_blacklist.findIndex((element) => {
                    return (
                        element.attribute_name === selectedAddValueAttribute &&
                        element.value === selectedEditValue
                    )
                })
            if (blacklistedAttributeIndex !== -1) {
                rule.attribute_blacklist.splice(blacklistedAttributeIndex, 1)
            }
        }

        setAttributes(tempAttributes)
        setBlockRules(tempBlockRules)
    }

    const isLastValue = () => {
        const curAttribute = attributes.find(
            (element) => element.attribute_name === selectedAddValueAttribute,
        )
        return curAttribute.possible_values.length === 1
    }

    const doesNoneValueExist = () => {
        const attribute = attributes.find(
            (element) => element.attribute_name === selectedAddValueAttribute,
        )
        if (!attribute) {
            return false
        }

        for (const curAttribute of attribute.possible_values) {
            if (curAttribute.value === 'none') {
                return true
            }
        }
        return false
    }

    const hasEmptyHashes = () => {
        if (ipfs === null || ipfs.length === 0) {
            return true
        }
        for (const layer of additionalLayers) {
            if (layer.ipfs === null || layer.ipfs.length === 0) {
                return true
            }
        }
        return false
    }

    const isLayerDuplicated = (layer) => {
        const allLayers = []
        for (const curLayer of additionalLayers) {
            allLayers.push(curLayer.layer)
        }
        const filtered = allLayers.filter((element) => element === layer)
        return filtered.length > 1
    }

    const hasDuplicateLayers = () => {
        const allLayers = []
        for (const curLayer of additionalLayers) {
            allLayers.push(curLayer.layer)
        }
        return allLayers.length !== new Set(allLayers).size
    }

    useEffect(() => {
        if (selectedAddValueAttribute !== '' && selectedEditValue !== '') {
            const curAttribute = attributes.find(
                (element) =>
                    element.attribute_name === selectedAddValueAttribute,
            )
            const curValue = curAttribute.possible_values.find(
                (element) => element.value === selectedEditValue,
            )
            setBaseLayer(curValue.layers[0].layer)
            setValue(curValue.value)
            setIpfs(curValue.layers[0].ipfs)
            if (curValue.layers.length > 1) {
                const temp = []
                for (let i = 1; i < curValue.layers.length; i++) {
                    temp.push({
                        layer: curValue.layers[i].layer,
                        ipfs: curValue.layers[i].ipfs,
                        width: curValue.layers[i].width,
                        height: curValue.layers[i].height,
                    })
                }
                setAdditionalLayers(temp)
            }
        }
    }, [selectedEditValue])

    useEffect(() => {
        if (layersLoaded.length === additionalLayers.length + 1) {
            setImageThatIsTooBigExists(false)
            for (let i = 0; i < layersLoaded.length; i++) {
                const loadedLayer = layersLoaded[i]
                const img = document.getElementById('img_layer_' + i)

                if (img === null) {
                    continue
                }

                const width = img.naturalWidth
                const height = img.naturalHeight

                if (
                    width > MAX_IMAGE_WIDTH_HEIGHT ||
                    height > MAX_IMAGE_WIDTH_HEIGHT
                ) {
                    setImageThatIsTooBigExists(true)
                }

                if (loadedLayer === baseLayer) {
                    setMainImageDims({
                        width: width,
                        height: height,
                    })
                } else {
                    const temp = [...additionalLayers]
                    const targetLayer = temp.find((element) => {
                        return element.layer === loadedLayer
                    })
                    if (targetLayer) {
                        targetLayer.width = width
                        targetLayer.height = height
                        setAdditionalLayers(temp)
                    }
                }
            }
        }
    }, [layersLoaded])

    const close = () => {
        if (!isUploading) {
            setValue('')
            setIpfs('')
            setLayersLoaded([])
            setMainImageDims(null)
            setAdditionalLayers([])
            setSelectedAddValueAttribute(null)
            setSelectedEditValue('')
            setError(null)
            setProgressPercentage(null)
            setIsUploading(false)
        }
    }

    return (
        <Dialog
            open={selectedAddValueAttribute !== null}
            onClose={() => {
                close()
            }}
            fullWidth
            maxWidth="sm"
        >
            <DropPaper
                noMargin
                title={
                    selectedEditValue !== '' ? (
                        <>
                            Edit value{' '}
                            <span className="text-primary">
                                {selectedEditValue}
                            </span>{' '}
                            of attribute{' '}
                            <span className="text-primary">
                                {selectedAddValueAttribute}
                            </span>
                        </>
                    ) : (
                        <>
                            Add value to attribute{' '}
                            <span className="text-primary">
                                {selectedAddValueAttribute}
                            </span>
                        </>
                    )
                }
            >
                <div>
                    <div className="flex flex-wrap justify-center mb-3">
                        <InputField
                            variant="bordered"
                            fullWidth
                            label="Value"
                            value={value ? value : ''}
                            isRequired={true}
                            isInvalid={value === null || value.length === 0}
                            description={
                                value === null || value.length === 0
                                    ? 'Value is required'
                                    : ' '
                            }
                            isDisabled={isUploading}
                            onChange={(e) => {
                                setValue(e.target.value)
                            }}
                        />
                    </div>
                    <div className="flex flex-wrap justify-center mb-3">
                        <div className="w-5/6">
                            <InputField
                                variant="bordered"
                                fullWidth
                                label="IPFS Hash"
                                value={ipfs ? ipfs : ''}
                                isRequired={true}
                                isInvalid={ipfs === null || ipfs.length === 0}
                                description={
                                    ipfs === null || ipfs.length === 0
                                        ? 'IPFS Hash is required'
                                        : mainImageDims
                                        ? `${mainImageDims.width}x${mainImageDims.height} pixels`
                                        : null
                                }
                                isDisabled={isUploading}
                                onChange={(e) => {
                                    let hash = e.target.value

                                    if (checkCID(hash)) {
                                        hash = ipfsToCID(hash)
                                    }

                                    setIpfs(hash)
                                }}
                            />
                        </div>
                        <div className="w-1/6">
                            <div className="flex justify-end flex-wrap w-full">
                                <div>
                                    <ImageUploadButton
                                        id={'addValue'}
                                        setIpfsHash={setIpfs}
                                        setError={setError}
                                        setProgressPercentage={
                                            setProgressPercentage
                                        }
                                        setIsUploading={setIsUploading}
                                        disabled={isUploading}
                                    />
                                </div>
                                <div>
                                    <IconButton
                                        color="primary"
                                        href={config.ipfs + ipfs}
                                        external={true}
                                        LinkComponent={DropLink}
                                        disabled={
                                            ipfs === null || ipfs.length === 0
                                        }
                                    >
                                        <PreviewOutlinedIcon />
                                    </IconButton>
                                </div>
                            </div>
                        </div>
                    </div>
                    <Collapse in={ipfs}>
                        <div className="flex justify-center">
                            <DropLink href={config.ipfs + ipfs} external>
                                <Tooltip
                                    title="Open image in a new tab"
                                    followCursor
                                >
                                    <PreviewImage
                                        attributes={attributes}
                                        ipfs={ipfs}
                                        selectedAddValueAttribute={
                                            selectedAddValueAttribute
                                        }
                                        additionalLayers={additionalLayers}
                                        layersLoaded={layersLoaded}
                                        setLayersLoaded={setLayersLoaded}
                                    />
                                </Tooltip>
                            </DropLink>
                        </div>
                    </Collapse>
                    <div className="border border-mui-light-dark rounded-md pt-1 px-3 pb-3 mt-3">
                        <div className="flex">
                            <div className="text-sm text-mui-light-dark my-auto">
                                Advanced - Multi Layer Attributes
                            </div>
                            <div className="my-auto">
                                <Tooltip
                                    title={
                                        <div>
                                            In some instances it can be required
                                            that a single attribute value may
                                            consist of several layers which need
                                            to be rendered in front of or behind
                                            other existing layers. This option
                                            allows to enhance this attribute
                                            with such layers, but is generally
                                            considered to be for advanced
                                            usecases only.
                                        </div>
                                    }
                                >
                                    <IconButton size="small" color="warning">
                                        <HelpOutlineOutlinedIcon />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        </div>
                        {additionalLayers.map((layer, index) => (
                            <div
                                key={'additional_layer_' + index}
                                className="flex flex-wrap justify-center mt-5"
                            >
                                <div className="w-1/2">
                                    <InputField
                                        variant="bordered"
                                        fullWidth
                                        label="IPFS Hash"
                                        value={layer.ipfs ? layer.ipfs : ''}
                                        isRequired={true}
                                        isInvalid={
                                            layer.ipfs === null ||
                                            layer.ipfs.length === 0
                                        }
                                        description={
                                            layer.ipfs === null ||
                                            layer.ipfs.length === 0
                                                ? 'IPFS Hash is required'
                                                : layer.width
                                                ? `${layer.width}x${layer.height} pixels`
                                                : null
                                        }
                                        isDisabled={isUploading}
                                        onChange={(e) => {
                                            updateAdditionalLayerIpfs(
                                                index,
                                                e.target.value,
                                            )
                                        }}
                                    />
                                </div>
                                <div className="w-1/2">
                                    <div className="flex justify-end flex-wrap w-full">
                                        <div className="w-28">
                                            <InputField
                                                variant="bordered"
                                                fullWidth
                                                label="Layer"
                                                value={
                                                    layer.layer !== null
                                                        ? layer.layer
                                                        : ''
                                                }
                                                type="number"
                                                onWheel={(e) => e.target.blur()}
                                                min={0}
                                                isInvalid={isLayerDuplicated(
                                                    layer.layer,
                                                )}
                                                onChange={(e) =>
                                                    updateAdditionalLayerLayer(
                                                        index,
                                                        e.target.value,
                                                    )
                                                }
                                            />
                                        </div>
                                        <div>
                                            <IconButton
                                                color="error"
                                                onClick={() =>
                                                    removeAdditionalLayer(index)
                                                }
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </div>
                                        <ImageUploadButton
                                            id={
                                                'additional_layer_upload_' +
                                                index
                                            }
                                            setIpfsHash={(ipfs) =>
                                                updateAdditionalLayerIpfs(
                                                    index,
                                                    ipfs,
                                                )
                                            }
                                            setError={setError}
                                            setProgressPercentage={
                                                setProgressPercentage
                                            }
                                            setIsUploading={setIsUploading}
                                            disabled={isUploading}
                                        />
                                        <div>
                                            <Link
                                                external={true}
                                                href={config.ipfs + layer.ipfs}
                                            >
                                                <IconButton
                                                    color="primary"
                                                    disabled={
                                                        layer.ipfs === null ||
                                                        layer.ipfs.length === 0
                                                    }
                                                >
                                                    <PreviewOutlinedIcon />
                                                </IconButton>
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                        <div className="mt-5">
                            <MainButton
                                className=" max-w-60"
                                size="small"
                                color="warning"
                                isDisabled={hasEmptyHashes()}
                                onClick={() => addAdditionalLayer()}
                            >
                                {' '}
                                Add additional layer
                            </MainButton>
                        </div>
                    </div>
                </div>
                {isUploading ? (
                    <div className="flex justify-center my-3">
                        <CircularProgress
                            variant="determinate"
                            value={progressPercentage}
                        />
                    </div>
                ) : null}
                <div className="text-center my-3">
                    {progressPercentage > 0 &&
                    progressPercentage < 100 &&
                    isUploading
                        ? 'Uploading...'
                        : progressPercentage === 100 && !ipfs
                        ? 'Waiting for IPFS Hash...'
                        : null}
                </div>
                <Collapse in={error !== null}>
                    <Alert severity="error">
                        <AlertTitle>An Error Occured!</AlertTitle>
                        {error}
                    </Alert>
                </Collapse>
                <DropAlert
                    condition={imageThatIsTooBigExists}
                    severity="warning"
                >
                    You are exceeding the recommended maximum width and / or
                    height of {MAX_IMAGE_WIDTH_HEIGHT} pixels!
                </DropAlert>
                <div className="flex flex-wrap justify-center my-8">
                    <div className="w-40 mx-2 my-2 md:my-0">
                        <MainButton
                            fullWidth
                            size="small"
                            isDisabled={!value || hasEmptyHashes()}
                            onClick={() => {
                                {
                                    selectedEditValue
                                        ? updateValue()
                                        : addValue(value, ipfs)
                                }
                                close()
                            }}
                        >
                            {' '}
                            {selectedEditValue ? 'Update Value' : 'Add Value'}
                        </MainButton>
                    </div>
                    {selectedEditValue ? (
                        <div className="w-40 mx-2 my-2 md:my-0">
                            <MainButton
                                size="small"
                                color="danger"
                                isDisabled={isLastValue()}
                                onClick={() => {
                                    deleteValue()
                                    close()
                                }}
                            >
                                Delete Value
                            </MainButton>
                        </div>
                    ) : null}
                    <div className="w-40 mx-2 my-2 md:my-0">
                        <MainButton
                            size="small"
                            fullWidth
                            color="danger"
                            onClick={() => {
                                close()
                            }}
                        >
                            {' '}
                            Cancel
                        </MainButton>
                    </div>
                </div>
                <DropAlert condition={hasDuplicateLayers()} severity="error">
                    Duplicate Layers detected!
                </DropAlert>
                <Collapse in={!doesNoneValueExist()}>
                    <div className="mb-2">
                        <Alert severity="info">
                            You can also add a special{' '}
                            <span className="font-mono">none</span> value
                            without an image, which represents an empty image
                        </Alert>
                    </div>
                    <div className="flex justify-center">
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                                addValue('none', '')
                                close()
                            }}
                        >
                            Add Empty Value
                        </Button>
                    </div>
                </Collapse>
            </DropPaper>
        </Dialog>
    )
}
